import { Component, OnInit } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { MaintenanceService } from '../../../services/maintenance.service';
import { BusinessService } from '../../../services/business.service';
import {
  BusinessMaintenance, MAINTENANCE_CHANNELS, MAINTENANCE_DEFAULT_MESSAGE, MAINTENANCE_MESSAGE_MAX,
  MaintenanceChannel, MaintenanceChannelCopy, MaintenanceNotice
} from '../../../models/maintenance.model';
import { BusinessModel } from '../../../models/user.model';
import { expectedEndProblem, toLocalInput, toRequest } from './maintenance-form';

/** One platform channel as the page shows it: its words and what is stored. */
export interface ChannelRow extends MaintenanceChannelCopy {
  notice: MaintenanceNotice | null;
}

/** What the inline form is for: closing or rewording a channel, or a business. */
type FormTarget = { kind: 'channel'; channel: MaintenanceChannel } | { kind: 'business'; businessId: string; businessName: string | null };

/**
 * Platform › Maintenance (specs/maintenance): put the desktop app, the mobile app or one business under maintenance,
 * say what the apps show and when you expect to be done, and lift it. Nothing here happens on one click: closing asks
 * first and says what it does. The apps and the other services see a change within seconds.
 */
@Component({
  selector: 'app-maintenance-page',
  standalone: false,
  templateUrl: './maintenance-page.component.html',
  styleUrls: ['./maintenance-page.component.css']
})
export class MaintenancePageComponent implements OnInit {
  readonly defaultMessage = MAINTENANCE_DEFAULT_MESSAGE;
  readonly messageMax = MAINTENANCE_MESSAGE_MAX;

  channels: ChannelRow[] = MAINTENANCE_CHANNELS.map(c => ({ ...c, notice: null }));
  businesses: BusinessMaintenance[] = [];
  loading = false;
  error = '';
  notice = '';
  /** Set when the account API cannot serve maintenance at all (404 before this release, no answer); replaces the page. */
  unavailable = '';

  /** The inline form, when open. */
  form: FormTarget | null = null;
  message = '';
  expectedEndLocal = '';
  saving = false;

  /** The lift waiting for its confirmation. */
  lifting: FormTarget | null = null;

  /** The business picker for "Put a business under maintenance". */
  businessQuery = '';
  allBusinesses: BusinessModel[] = [];
  private businessesLoaded = false;

  constructor(private maintenance: MaintenanceService, private businessService: BusinessService) {}

  ngOnInit(): void {
    this.load();
  }

  load(): void {
    this.loading = true;
    this.error = '';
    this.unavailable = '';
    this.maintenance.overview().subscribe({
      next: overview => {
        this.loading = false;
        this.channels = MAINTENANCE_CHANNELS.map(c => ({ ...c, notice: c.key === 'desktop' ? overview.desktop : overview.mobile }));
        this.businesses = overview.businesses || [];
      },
      error: (err: HttpErrorResponse) => {
        this.loading = false;
        if (err?.status === 404) {
          this.unavailable = 'The account API has no maintenance mode yet (this release is not deployed there).';
        } else if (err?.status === 0) {
          this.unavailable = 'The account API did not answer (network or CORS).';
        } else {
          this.error = this.describe(err, 'Could not load the maintenance state.');
        }
      }
    });
  }

  // ---- the inline form

  openChannelForm(row: ChannelRow): void {
    this.reset();
    this.form = { kind: 'channel', channel: row.key };
    this.message = row.notice?.message ?? '';
    this.expectedEndLocal = toLocalInput(row.notice?.expectedEndUtc);
  }

  openBusinessForm(business: BusinessMaintenance): void {
    this.reset();
    this.form = { kind: 'business', businessId: business.businessId, businessName: business.businessName };
    this.message = business.message ?? '';
    this.expectedEndLocal = toLocalInput(business.expectedEndUtc);
  }

  /** From the picker: the typed id, or the one business whose name or id matches. */
  openPickedBusinessForm(): void {
    const picked = this.pickedBusiness();
    if (!picked) {
      this.error = 'Pick a business first: type its name or id.';
      return;
    }
    this.reset();
    this.form = { kind: 'business', businessId: picked.businessId, businessName: picked.name };
  }

  pickedBusiness(): { businessId: string; name: string | null } | null {
    const query = this.businessQuery.trim().toLowerCase();
    if (!query) return null;
    const exact = this.allBusinesses.find(b => String(b.businessId).toLowerCase() === query || (b.name || '').toLowerCase() === query);
    if (exact) return { businessId: String(exact.businessId), name: exact.name };
    const matches = this.allBusinesses.filter(b => (b.name || '').toLowerCase().includes(query));
    if (matches.length === 1) return { businessId: String(matches[0].businessId), name: matches[0].name };
    // An id the list does not know (the list is only Plus businesses): let the API decide.
    return /^[0-9a-f-]{20,}$/i.test(query) ? { businessId: this.businessQuery.trim(), name: null } : null;
  }

  loadBusinessesForPicker(): void {
    if (this.businessesLoaded) return;
    this.businessesLoaded = true;
    this.businessService.getBusinesses().subscribe({
      next: list => (this.allBusinesses = list || []),
      error: () => { this.businessesLoaded = false; }
    });
  }

  get expectedEndError(): string {
    return expectedEndProblem(this.expectedEndLocal);
  }

  /** The target already is under maintenance: the form rewords rather than closes. */
  get rewording(): boolean {
    const target = this.form;
    if (!target) return false;
    return target.kind === 'channel'
      ? !!this.channels.find(c => c.key === target.channel)?.notice
      : this.businesses.some(b => b.businessId === target.businessId);
  }

  targetName(target: FormTarget | null): string {
    if (!target) return '';
    return target.kind === 'channel'
      ? (MAINTENANCE_CHANNELS.find(c => c.key === target.channel)?.name ?? target.channel)
      : (target.businessName || target.businessId);
  }

  confirmForm(): void {
    const target = this.form;
    if (!target || this.saving || this.expectedEndError) return;
    this.saving = true;
    this.error = '';
    const request = toRequest(this.message, this.expectedEndLocal);
    const done = (what: string) => {
      this.saving = false;
      this.notice = what;
      this.form = null;
      this.businessQuery = '';
      this.load();
    };
    const fail = (err: HttpErrorResponse, what: string) => {
      this.saving = false;
      this.error = this.describe(err, what);
    };
    if (target.kind === 'channel') {
      this.maintenance.setPlatform(target.channel, request).subscribe({
        next: () => done(`${this.targetName(target)} is under maintenance for every business. Tills see the page within seconds.`),
        error: err => fail(err, `Could not put the ${this.targetName(target).toLowerCase()} under maintenance.`)
      });
    } else {
      this.maintenance.setBusiness(target.businessId, request).subscribe({
        next: saved => done(`${saved.businessName || saved.businessId} is under maintenance on every channel.`),
        error: err => fail(err, `Could not put ${this.targetName(target)} under maintenance.`)
      });
    }
  }

  cancelForm(): void {
    this.form = null;
  }

  // ---- lifting

  askLiftChannel(row: ChannelRow): void {
    this.reset();
    this.lifting = { kind: 'channel', channel: row.key };
  }

  askLiftBusiness(business: BusinessMaintenance): void {
    this.reset();
    this.lifting = { kind: 'business', businessId: business.businessId, businessName: business.businessName };
  }

  confirmLift(): void {
    const target = this.lifting;
    if (!target || this.saving) return;
    this.saving = true;
    this.error = '';
    const call = target.kind === 'channel' ? this.maintenance.clearPlatform(target.channel) : this.maintenance.clearBusiness(target.businessId);
    call.subscribe({
      next: () => {
        this.saving = false;
        this.lifting = null;
        this.notice = `${this.targetName(target)} is open again.`;
        this.load();
      },
      error: (err: HttpErrorResponse) => {
        this.saving = false;
        this.error = this.describe(err, `Could not lift maintenance for ${this.targetName(target)}.`);
      }
    });
  }

  cancelLift(): void {
    this.lifting = null;
  }

  isLifting(target: FormTarget): boolean {
    const current = this.lifting;
    if (!current || current.kind !== target.kind) return false;
    return current.kind === 'channel' ? current.channel === (target as { channel: MaintenanceChannel }).channel
      : current.businessId === (target as { businessId: string }).businessId;
  }

  isForming(target: FormTarget): boolean {
    const current = this.form;
    if (!current || current.kind !== target.kind) return false;
    return current.kind === 'channel' ? current.channel === (target as { channel: MaintenanceChannel }).channel
      : current.businessId === (target as { businessId: string }).businessId;
  }

  channelTarget(row: ChannelRow): FormTarget { return { kind: 'channel', channel: row.key }; }
  businessTarget(b: BusinessMaintenance): FormTarget { return { kind: 'business', businessId: b.businessId, businessName: b.businessName }; }

  /** An ISO instant for the date pipe, with the Z the API sometimes leaves off. */
  utc(value: string | null | undefined): string | null {
    if (!value) return null;
    return /(Z|[+-]\d{2}:?\d{2})$/i.test(value) ? value : `${value}Z`;
  }

  private reset(): void {
    this.form = null;
    this.lifting = null;
    this.error = '';
    this.notice = '';
    this.message = '';
    this.expectedEndLocal = '';
  }

  private describe(err: HttpErrorResponse, fallback: string): string {
    const body = err?.error;
    if (typeof body === 'string' && body.trim()) return body;
    if (body?.message) return body.message;
    if (err?.status === 403) return `${fallback} Only a SuperAdmin may change maintenance.`;
    if (err?.status === 0) return `${fallback} The account API did not answer (network or CORS).`;
    return fallback;
  }
}
